import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, catchError } from 'rxjs';
import { PlatformConfig } from 'src/environments/environment.base';

export class ApiResponse<T> {
  constructor(public data: T, public meta?: any) {}
}

export class Pagination {
  constructor(
    public current_page: number,
    public from: number,
    public last_page: number,
    public path: string,
    public per_page: number,
    public to: number,
    public total: number
  ) {}
}

export class PaginatedResponse<T> {
  constructor(public data: T[], public meta: Pagination) {}
}

export class ErrorResponse<T> implements Error {
  constructor(
    public name: string,
    public errors: T, // API-generated error object
    public message: string // API-generated error message, or an internal error message e.g. from HttpErrorResponse
  ) {}
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) {}

  get<T>(slug: string, queryParams: any = {}): Observable<T> {
    let params = new HttpParams();

    Object.keys(queryParams).forEach((param: string) => {
      if (queryParams[param] instanceof Array) {
        queryParams[param].forEach((item: string) => {
          params = params.append(param + '[]', item);
        });
      } else {
        if (queryParams[param]) {
          params = params.append(param, queryParams[param]);
        }
      }
    });

    return this.request('get', slug, params);
  }

  getFile(slug: string) {
    return this.http.get(this.url(slug), {
      responseType: 'blob' as 'json',
      headers: this.requestHeaders,
      withCredentials: true,
    });
  }

  post<T>(slug: string, params: any): Observable<T> {
    return this.request('post', slug, new HttpParams(), params);
  }

  patch<T>(slug: string, params: any): Observable<T> {
    return this.request('patch', slug, new HttpParams(), params);
  }

  delete<T>(slug: string): Observable<T> {
    return this.request('delete', slug);
  }

  get domainTokenObject() {
    const domain = window.location.hostname;

    return environment.platformConfigs.find(
      (pt: PlatformConfig) => pt.domain === domain
    );
  }

  get getPlatformToken() {
    const platformConfig = this.domainTokenObject;

    if (platformConfig) {
      return platformConfig.token;
    }

    return '';
  }

  private url(slug: string): string {
    let baseUrl: string = '';

    const inputDomainObject = this.domainTokenObject;

    if (inputDomainObject) {
      baseUrl = inputDomainObject.api;
    }

    return baseUrl + '/' + slug;
  }

  get requestHeaders() {
    let headers = new HttpHeaders();
    headers = headers.set('X-Platform-Token', this.getPlatformToken);
    return headers;
  }

  private request<T>(
    method: string,
    slug: string,
    params: HttpParams = new HttpParams(),
    body: any = {}
  ): Observable<T> {
    return this.http
      .request<T>(method, this.url(slug), {
        params,
        body,
        headers: this.requestHeaders,
        withCredentials: true,
      })
      .pipe(
        catchError((err: any) => {
          const error = new ErrorResponse(
            err.name,
            err.error?.errors,
            err.error.message ? err.error.message : err.message
          );
          return throwError(() => error);
        })
      );
  }
}
